import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowLeft, User } from 'lucide-react';

const JournalDetailPage = () => {
  const { id } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Mock data - in real app, fetch from API
  const post = {
    'maldives-luxury-guide': {
      title: 'The Ultimate Maldives Luxury Guide',
      excerpt: 'Discover the most exclusive resorts, private islands, and underwater dining experiences in the Maldives.',
      image: 'https://images.unsplash.com/photo-1620483829646-8fb1d8162108?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxtYWxkaXZlcyUyMGx1eHVyeSUyMHdhdGVyJTIwdmlsbGElMjBvY2VhbiUyMGFlcmlhbHxlbnwwfHx8fDE3NzMxNDQ1NTR8MA&ixlib=rb-4.1.0&q=85',
      category: 'Destination Guides',
      date: 'January 15, 2026',
      author: 'Sarvatra Editorial Team',
      content: `The Maldives remains the ultimate destination for luxury travelers seeking pristine beaches, crystal-clear waters, and unparalleled service. This archipelago of 1,192 islands offers some of the world's most exclusive resort experiences.

## Private Island Resorts

The pinnacle of Maldivian luxury lies in private island resorts. Properties like Soneva Jani and Velaa Private Island offer complete seclusion with overwater villas, personal butler service, and bespoke experiences tailored to each guest.

## Underwater Dining

The Maldives pioneered underwater dining experiences. Restaurants like Ithaa at Conrad Maldives and 5.8 at Hurawalhi offer immersive culinary journeys surrounded by marine life.

## Water Villas

Iconic overwater villas with direct lagoon access define Maldivian luxury. Many feature private infinity pools, glass floor panels for marine viewing, and outdoor decks for stargazing.

## Best Time to Visit

The dry season from November to April offers the best weather, though luxury resorts provide exceptional experiences year-round.

## Sustainability

Leading resorts now emphasize sustainability, with coral restoration programs, zero-waste initiatives, and renewable energy.

The Maldives continues to redefine luxury travel, offering experiences that blend natural beauty with world-class hospitality.`,
    },
  }[id] || {};

  if (!post.title) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-primary text-lg">Post not found</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="journal-detail-page"
    >
      {/* Hero */}
      <section className="relative h-[70vh] flex items-end overflow-hidden" data-testid="journal-detail-hero">
        <div className="absolute inset-0">
          <img
            src={post.image}
            alt={post.title}
            className="hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        </div>

        <div className="relative z-10 container-padding pb-16 max-w-4xl mx-auto w-full">
          <Link
            to="/journal"
            className="inline-flex items-center gap-2 text-primary/80 hover:text-primary text-sm mb-8 transition-colors duration-300"
            data-testid="back-to-journal-link"
          >
            <ArrowLeft size={16} /> Back to Journal
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <p className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-4">
              {post.category}
            </p>

            <h1 className="font-heading text-4xl md:text-6xl font-light tracking-tight text-primary mb-6" data-testid="journal-detail-title">
              {post.title}
            </h1>

            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <User size={14} className="text-accent" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={14} className="text-accent" />
                <span>{post.date}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding container-padding" data-testid="journal-detail-content">
        <div className="max-w-3xl mx-auto">
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="prose prose-invert prose-lg max-w-none"
          >
            <p className="text-xl text-muted-foreground leading-relaxed mb-12">
              {post.excerpt}
            </p>

            <div className="text-muted-foreground leading-relaxed space-y-6">
              {post.content.split('\n\n').map((paragraph, index) => {
                if (paragraph.startsWith('## ')) {
                  return (
                    <h2 key={index} className="font-heading text-3xl font-light text-primary mt-12 mb-6">
                      {paragraph.replace('## ', '')}
                    </h2>
                  );
                }
                return (
                  <p key={index} className="text-base">
                    {paragraph}
                  </p>
                );
              })}
            </div>
          </motion.article>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding container-padding bg-white/[0.02] text-center" data-testid="journal-detail-cta">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mx-auto"
        >
          <h2 className="font-heading text-4xl md:text-5xl font-light text-primary mb-6">
            Ready to Experience This Destination?
          </h2>
          <p className="text-muted-foreground text-lg mb-10">
            Let our travel advisors craft your perfect journey.
          </p>
          <Link to="/concierge" className="btn-primary" data-testid="journal-plan-journey-btn">
            Plan Your Journey
          </Link>
        </motion.div>
      </section>
    </motion.div>
  );
};

export default JournalDetailPage;